import React from 'react';
import { CheckCircleIcon } from '@heroicons/react/24/outline';
import Button from '@/ui/components/shared/Button';
import { paths } from '@/constants/paths';
import { useTranslations } from 'next-intl';
import { UniversalPaymentStatusEnum } from 'typescript-node-phoenix-payments/dist/model/universalPaymentStatusEnum';

interface OrderSuccessProps {
  orderReference: string;
  orderId: string;
  isOrderVirtual: boolean;
  paymentStatus: UniversalPaymentStatusEnum | null;
  /** Optional CMS overrides — each falls back to the default localized copy when absent. */
  title?: string;
  body?: React.ReactNode;
  ctaLabel?: string;
  ctaHref?: string;
}

export function OrderSuccess({
  orderReference,
  orderId,
  isOrderVirtual,
  paymentStatus,
  title,
  body,
  ctaLabel,
  ctaHref,
}: Readonly<OrderSuccessProps>) {
  const t = useTranslations('OrderSuccess');

  return (
    <div className="flex lg:gap-3">
      <CheckCircleIcon className="hidden shrink-0 lg:block lg:h-12" />
      <div className="lg:mt-1">
        <div className="flex gap-2">
          <CheckCircleIcon className="mt-1 h-8 shrink-0 lg:hidden" />
          <div>
            <p className="text-sm text-neutral-50 sm:text-base">
              {t('Order orderReference', { orderReference })}
            </p>
            <h2 className="text-lg font-medium sm:text-lg md:text-2xl">
              {title ?? t('Thank you for your order')}
            </h2>
          </div>
        </div>
        <div className="text-md">
          {body ?? (
            <>
              <p className="mt-2">
                {isOrderVirtual
                  ? t('Your order is now completed')
                  : t('We are getting your order ready to be shipped')}
              </p>
              <p>
                {t('You can see your details in')}{' '}
                <a href={paths.myAccount} className="underline">
                  {t('your account')}
                </a>
              </p>
            </>
          )}
        </div>
        <div className="flex items-center justify-between">
          <Button href={ctaHref ?? `${paths.orders}/${orderId}`} className="mt-4 w-fit">
            {ctaLabel ?? t('View order Details')}
          </Button>
          {(paymentStatus === UniversalPaymentStatusEnum.Open ||
            paymentStatus === UniversalPaymentStatusEnum.Pending) && (
            <div className="mt-3">
              <span className="mr-2 font-medium">{t('Payment Status:')}</span>
              <span className="pulse-animation rounded-full bg-primary-20 px-3 py-1 text-center font-bold text-primary-100">
                {paymentStatus}
              </span>
            </div>
          )}
          <div />
        </div>
      </div>
    </div>
  );
}
